import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
  useTheme,
  type Theme,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseFastApiValidationMessage } from '../helpers/api-error.helper';
import { getHelpFontFamily } from '../constants/help-typography';
import { helpI18n, isHelpArabic } from '../i18n/help';
import { submitContactRequest } from '../services/contact.service';
import type { ContactReason } from '../types/contact.type';

const TITLE_MAX = 200;
const MESSAGE_MAX = 2000;

const CONTACT_REASONS: ContactReason[] = [
  'technical_support',
  'pickup_orders',
  'fleet_drivers',
  'containers_sites',
  'complaints_suggestions',
  'general_inquiry',
];

const getDialogColors = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    isDark,
    surface: isDark ? '#1D232B' : theme.palette.background.paper,
    fieldBg: isDark ? '#262E37' : '#F8FAFC',
    fieldBgFocused: isDark ? '#2E3742' : '#FFFFFF',
    label: theme.palette.text.secondary,
    text: theme.palette.text.primary,
    placeholder: theme.palette.text.disabled,
    border: theme.palette.divider,
    borderHover: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.26)' : theme.palette.text.disabled,
    focusRing: isDark ? '0 0 0 3px rgba(244, 132, 26, 0.18)' : '0 0 0 3px rgba(244, 132, 26, 0.12)',
    cancelHoverBg: isDark ? 'rgba(255, 255, 255, 0.06)' : '#F8FAFC',
  };
};

const getFieldSx = (theme: Theme) => {
  const colors = getDialogColors(theme);

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: colors.fieldBg,
      color: colors.text,
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
      '& input, & textarea': {
        color: colors.text,
        fontSize: '0.9375rem',
        lineHeight: 1.5,
        '&::placeholder': {
          color: colors.placeholder,
          opacity: 1,
        },
      },
      '& fieldset': {
        borderColor: colors.border,
      },
      '&:hover fieldset': {
        borderColor: colors.borderHover,
      },
      '&.Mui-focused': {
        bgcolor: colors.fieldBgFocused,
        boxShadow: colors.focusRing,
        '& fieldset': {
          borderColor: 'primary.main',
          borderWidth: '1px',
        },
      },
    },
    '& .MuiSelect-icon': {
      color: colors.placeholder,
    },
  };
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const colors = getDialogColors(theme);

  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        color: colors.label,
        letterSpacing: 0,
        lineHeight: 1.4,
        mb: 1.25,
        display: 'block',
      }}
    >
      {children}
      <Typography component="span" sx={{ color: '#F87171', ml: 0.25 }}>
        *
      </Typography>
    </Typography>
  );
}

interface ContactSupportButtonProps {
  organizationId: string;
  variant?: 'floating' | 'inline';
  /** Reserved for Knowledge Base header actions grouping. */
  useHelpI18n?: boolean;
}

const ContactSupportButton: React.FC<ContactSupportButtonProps> = ({
  organizationId,
  variant = 'floating',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', { i18n: helpI18n });
  const rtl = isHelpArabic();
  const colors = getDialogColors(theme);
  const fieldSx = getFieldSx(theme);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [reason, setReason] = useState<ContactReason | ''>('');
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'error',
  });

  const resetForm = useCallback(() => {
    setTitle('');
    setReason('');
    setMessage('');
    setFormError(null);
  }, []);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const mutation = useMutation({
    mutationFn: () =>
      submitContactRequest({
        organization_id: organizationId,
        title: title.trim(),
        reason: reason as ContactReason,
        message: message.trim(),
      }),
    onSuccess: () => {
      resetForm();
      setOpen(false);
      setSnackbar({
        open: true,
        message: t('contact.successMessage'),
        severity: 'success',
      });
    },
    onError: (error: unknown) => {
      const ax = error as AxiosError<{ detail?: unknown }>;
      const status = ax.response?.status;
      const detail = ax.response?.data?.detail;
      if (status === 422) {
        setFormError(parseFastApiValidationMessage(detail));
        return;
      }
      setSnackbar({
        open: true,
        message:
          (typeof detail === 'string' ? detail : parseFastApiValidationMessage(detail)) ||
          ax.message ||
          t('contact.submitError'),
        severity: 'error',
      });
    },
  });

  const handleTriggerClick = () => {
    if (variant === 'floating') {
      navigate('/knowledge-base');
      return;
    }
    setOpen(true);
  };

  const handleDialogClose = () => {
    if (mutation.isPending) return;
    setOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmedTitle = title.trim();
    const trimmedMessage = message.trim();

    if (!trimmedTitle) {
      setFormError(t('contact.subjectRequired'));
      return;
    }
    if (trimmedTitle.length > TITLE_MAX) {
      setFormError(t('contact.subjectMax', { max: TITLE_MAX }));
      return;
    }
    if (!reason) {
      setFormError(t('contact.categoryRequired'));
      return;
    }
    if (!trimmedMessage) {
      setFormError(t('contact.messageRequired'));
      return;
    }
    if (trimmedMessage.length > MESSAGE_MAX) {
      setFormError(t('contact.messageMax', { max: MESSAGE_MAX }));
      return;
    }

    mutation.mutate();
  };

  const triggerButton =
    variant === 'floating' ? (
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        <Button
          onClick={handleTriggerClick}
          startIcon={<HelpOutlineOutlinedIcon />}
          aria-label={t('contact.help')}
          sx={{
            px: 2.5,
            py: 1,
            bgcolor: theme.palette.background.paper,
            color: 'primary.main',
            fontFamily: getHelpFontFamily(helpI18n.language),
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '10px',
            boxShadow: `0 4px 20px ${theme.palette.primary.main}33`,
            border: `1px solid ${theme.palette.primary.main}1F`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: theme.palette.background.paper,
              transform: 'scale(1.04)',
              boxShadow: `0 6px 24px ${theme.palette.primary.main}47`,
            },
          }}
        >
          {t('contact.help')}
        </Button>
      </Box>
    ) : (
      <Button
        onClick={handleTriggerClick}
        variant="contained"
        aria-label={t('contact.title')}
        sx={{
          borderRadius: '10px',
          px: 2.5,
          fontFamily: getHelpFontFamily(helpI18n.language),
          fontWeight: 600,
          fontSize: '0.9375rem',
          textTransform: 'none',
          boxShadow: `0 4px 14px ${theme.palette.primary.main}3D`,
          '&:hover': {
            boxShadow: `0 6px 18px ${theme.palette.primary.main}52`,
          },
        }}
      >
        {t('contact.title')}
      </Button>
    );

  return (
    <>
      {triggerButton}

      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        dir={rtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            overflow: 'hidden',
            bgcolor: colors.surface,
            fontFamily: getHelpFontFamily(helpI18n.language),
            boxShadow: colors.isDark
              ? '0 24px 48px rgba(0, 0, 0, 0.45)'
              : '0 20px 60px rgba(15, 23, 42, 0.14)',
            '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root': {
              fontFamily: 'inherit',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(255, 255, 255, 0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1, m: 0 }}>
              {t('contact.title')}
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{
              px: 3.5,
              py: 3.5,
              bgcolor: colors.surface,
              color: colors.text,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {formError ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: '12px',
                  bgcolor: colors.isDark ? 'rgba(239, 68, 68, 0.12)' : undefined,
                  color: colors.isDark ? 'rgba(255, 255, 255, 0.9)' : undefined,
                  '& .MuiAlert-icon': {
                    color: colors.isDark ? '#FCA5A5' : undefined,
                  },
                }}
              >
                {formError}
              </Alert>
            ) : null}
            <Box>
              <FieldLabel>{t('contact.category')}</FieldLabel>
              <FormControl fullWidth sx={fieldSx}>
                <Select
                  id="contact-reason"
                  value={reason}
                  displayEmpty
                  onChange={(e: SelectChangeEvent<ContactReason | ''>) =>
                    setReason(e.target.value as ContactReason | '')
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body2" sx={{ color: colors.placeholder }}>
                          {t('contact.selectCategory')}
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body2" sx={{ color: colors.text }}>
                        {t(`contact.reasons.${selected}`)}
                      </Typography>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: colors.surface,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '12px',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          color: colors.text,
                          fontSize: '0.9375rem',
                          py: 1.25,
                          '&:hover': {
                            bgcolor: colors.isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&.Mui-selected': {
                            bgcolor: colors.isDark ? 'rgba(244, 132, 26, 0.16)' : 'rgba(244, 132, 26, 0.1)',
                            '&:hover': {
                              bgcolor: colors.isDark ? 'rgba(244, 132, 26, 0.22)' : 'rgba(244, 132, 26, 0.14)',
                            },
                          },
                        },
                      },
                    },
                  }}
                  inputProps={{ 'aria-label': t('contact.category') }}
                >
                  {CONTACT_REASONS.map((reasonValue) => (
                    <MenuItem key={reasonValue} value={reasonValue}>
                      {t(`contact.reasons.${reasonValue}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <FieldLabel>{t('contact.subject')}</FieldLabel>
              <TextField
                value={title}
                onChange={(ev) => setTitle(ev.target.value)}
                inputProps={{ maxLength: TITLE_MAX, 'aria-label': t('contact.subject') }}
                fullWidth
                hiddenLabel
                variant="outlined"
                placeholder={t('contact.subjectPlaceholder')}
                sx={fieldSx}
              />
            </Box>

            <Box>
              <FieldLabel>{t('contact.messageDetails')}</FieldLabel>
              <TextField
                value={message}
                onChange={(ev) => setMessage(ev.target.value)}
                inputProps={{ maxLength: MESSAGE_MAX, 'aria-label': t('contact.messageDetails') }}
                fullWidth
                hiddenLabel
                multiline
                minRows={5}
                variant="outlined"
                placeholder={t('contact.messagePlaceholder')}
                sx={fieldSx}
              />
            </Box>
          </DialogContent>
          <Divider sx={{ borderColor: theme.palette.divider }} />
          <DialogActions
            sx={{
              px: 3.5,
              py: 2.5,
              gap: 1.5,
              bgcolor: colors.surface,
            }}
          >
            <Button
              onClick={handleDialogClose}
              disabled={mutation.isPending}
              variant="outlined"
              sx={{
                borderRadius: '12px',
                px: 2.75,
                py: 1,
                borderColor: colors.border,
                color: colors.label,
                '&:hover': {
                  borderColor: colors.borderHover,
                  bgcolor: colors.cancelHoverBg,
                },
              }}
            >
              {t('contact.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={mutation.isPending}
              startIcon={mutation.isPending ? undefined : <SendOutlinedIcon />}
              sx={{
                borderRadius: '12px',
                px: 3.25,
                py: 1,
                boxShadow: `0 4px 14px ${theme.palette.primary.main}3D`,
                '&:hover': {
                  boxShadow: `0 6px 18px ${theme.palette.primary.main}52`,
                },
              }}
            >
              {mutation.isPending ? t('contact.submittingLabel') : t('contact.submitLabel')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactSupportButton;
